import { useQuantitativeDataSummaryStats } from 'features/dataLayers'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useFfsComponentCdfs } from 'features/libra/stepComponents/ffs/useFfsComponentCdfs'
import { useMemo } from 'react'

interface UseObservedMinThicknessArgs {
  componentId: string | undefined
  inspectionId: string | undefined
}

/**
 * The observed minimum is either available or it isn't, and when it isn't there
 * are three distinct reasons the engineer needs told apart. Modelling that as
 * one tagged union rather than a bag of booleans keeps contradictory states
 * (`noCdf` AND `failed`) unrepresentable, and lets both the step's validation
 * and the panel's rendering switch on a single value.
 */
export type ObservedMinThickness =
  /** Queries still in flight. Transient. */
  | { status: 'loading' }
  /** No thickness CDF for this inspection -- nothing to measure against. */
  | { status: 'noCdf' }
  /**
   * A thickness CDF exists but its readings could not be loaded -- typically
   * it has no parquet file, so no signed URL resolves. Terminal, not
   * transient: without this case a failure is indistinguishable from
   * 'loading', which is exactly what made the original hang so hard to see.
   */
  | { status: 'unreadable' }
  | { status: 'ready'; observedMinMm: number }

/**
 * Returns the observed minimum thickness from the inspection's thickness CDF.
 *
 * Delegates to `useQuantitativeDataSummaryStats`, whose cache is keyed by
 * `cdf.id`. Because `useFfsComponentCdfs` resolves the thickness layer through
 * the same `getCoarsestCdf` pick that `FfsAssetVizPanel` makes for the viewer,
 * both surfaces land on ONE cache entry and this check rides the stats the viz
 * has already computed instead of starting a second full-grid parquet read.
 *
 * That sharing is load-bearing, not incidental: when the two picks diverged,
 * this hook silently recomputed the entire bin grid in a worker to obtain a
 * single scalar. Keep the picks together.
 *
 * Note `value_range.min` is the minimum over the *binned* grid, so it equals
 * "the lowest point in the CDS" only when the layer was dispensed with a
 * min-thickness bin aggregation.
 */
export function useObservedMinThickness({
  componentId,
  inspectionId,
}: UseObservedMinThicknessArgs): ObservedMinThickness {
  // `cdfsLoading`, not `isLoading` -- the latter also waits on the asset-model
  // snapshot, which this check never reads.
  const { thicknessCdf, cdfsLoading } = useFfsComponentCdfs({
    componentId,
    inspectionId,
  })
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useQuantitativeDataSummaryStats(thicknessCdf)

  return useMemo((): ObservedMinThickness => {
    if (statsError !== undefined) return { status: 'unreadable' }
    if (cdfsLoading || statsLoading) return { status: 'loading' }
    if (!thicknessCdf) return { status: 'noCdf' }

    const observedMinMm = stats?.value_range?.min?.convertTo(
      WORKING_UNIT_THICKNESS
    ).num
    if (observedMinMm === undefined) return { status: 'unreadable' }

    return { status: 'ready', observedMinMm }
  }, [cdfsLoading, statsLoading, statsError, stats, thicknessCdf])
}
